import template from './manage-subscription.html';

class ManageSubscriptionVM
{
	constructor(page)
	{
		this.page = page;

		// ko-render-fields params
		this.inputs = ko.observable({});
		this.fields = ko.observableArray([]);
		this.initialValue = ko.observable({});

		// Event selectors
		this.event_namespaces = ko.observableArray();
		this.selected_namespace = ko.observable();
		this.event_objects = ko.observableArray();
		this.selected_object = ko.observable();
		this.event_names = ko.observableArray();
		this.selected_name = ko.observable();
		this.raw_event_data = ko.observableArray();

		// Actions
		this.actions = ko.observableArray();
		this.selected_action_qname = ko.observable();
		this.raw_action_data = ko.observableArray();

		// Subscription fields
		this.subscription_uuid = ko.observable('');
		this.active = ko.observable(true);
		this.idx = ko.observable(0);
		this.defer = ko.observable(false);
		this.event_data_match = ko.observable('');
		this.json_map_definition_id = ko.observable(null);

		// Stats & info
		this.subscription_detail = ko.observable(null);
		this.action_logs = ko.observableArray([]);
		this.event_info = ko.observable(null);
		this.action_info = ko.observable(null);
		this.showActionLogs = ko.observable(true);

		this.isCreateMode = ko.observable(false);
		this.loading = ko.observable(false);

		this.selected_namespace.subscribe(() => this.page.getEventObjectTypes());
		this.selected_object.subscribe(() => this.page.getEventNames());

		this.selected_action_values = ko.computed(() => {
			const actions = this.actions();
			const selected = actions.find(x => x.action_qname === this.selected_action_qname());

			if (typeof selected !== 'undefined')
				this.fields(selected.ui_fields || []);

			return selected;
		});

		this.selected_action_qname.subscribe((qname) => {
			if (qname)
				this.page.loadActionInfo(qname);
		});

		this.selected_name.subscribe(() => {
			const ns = this.selected_namespace();
			const ot = this.selected_object();
			const nm = this.selected_name();
			if (ns && ot && nm)
				this.page.loadEventInfo(ns, ot, nm);
		});
	}

	async btnSave()
	{
		try
		{
			let config_data = {};
			for (const [name, $input] of Object.entries(this.inputs()))
				config_data[name] = ($input)();

			if (!this.selected_namespace())
				throw new Error('No event chosen');
			if (!this.selected_action_qname())
				throw new Error('No action chosen');

			const payload = {
				subscription_uuid: this.subscription_uuid() || null,
				event_qname: `${this.selected_namespace()}.${this.selected_object()}.${this.selected_name()}`,
				action_qname: this.selected_action_qname(),
				active: this.active(),
				idx: this.idx(),
				defer: this.defer(),
				json_map_definition_id: this.json_map_definition_id() || null,
				config_data: config_data
			};

			const edm = this.event_data_match();
			try
			{
				if (edm)
					payload.event_data_match = JSON.parse(edm);
			}
			catch (err)
			{
				throw new Error('Event data match must be valid JSON');
			}

			const result = await Grape.fetches.postJSON('/api/events/subscription/', payload);
			if (result.status !== 'OK')
				throw new Error(result.message || 'Unable to save');

			await Grape.alerts.alert({ type: 'success', title: 'Saved', message: 'Subscription saved successfully' });

			if (this.isCreateMode() && result.subscription_uuid)
				window.location.hash = `/ui/events/subscription/${result.subscription_uuid}`;
			else
				this.page.updateData();
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
			console.error(err);
		}
	}

	btnBack()
	{
		window.location.hash = '/ui/events/subscriptions';
	}

	async btnDelete()
	{
		const confirmed = await Grape.alerts.confirm({
			type: 'warning',
			title: 'Delete Subscription',
			message: 'Are you sure you want to delete this subscription?'
		});
		if (!confirmed)
			return;

		try
		{
			await Grape.fetches.postJSON('/api/subscription/delete', {
				subscription_uuid: this.subscription_uuid()
			});
			window.location.hash = '/ui/events/subscriptions';
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	btnToggleActionLogs()
	{
		this.showActionLogs(!this.showActionLogs());
	}

	formatDate(dateStr)
	{
		if (!dateStr)
			return '-';
		return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
	}

	formatDateShort(dateStr)
	{
		if (!dateStr)
			return '-';
		return moment(dateStr).fromNow();
	}
}

class ManageSubscriptionPage
{
	constructor(bindings, element)
	{
		this.bindings = bindings;
		this.element = element;
		this.viewModel = new ManageSubscriptionVM(this);
		this.name = 'ManageSubscriptionPage';
	}

	async init()
	{
		document.title = 'Manage Subscription';
		this.viewModel.loading(true);

		const uuid = this.bindings.subscription_uuid;

		await this.loadEventNamespaces();
		await this.loadActions();

		if (!uuid || uuid === 'new')
		{
			this.viewModel.isCreateMode(true);
			this.viewModel.loading(false);
		}
		else
		{
			this.viewModel.isCreateMode(false);
			this.viewModel.subscription_uuid(uuid);
			await this.loadSubscription(uuid);
			this.viewModel.loading(false);
		}
	}

	async updateData()
	{
		const uuid = this.viewModel.subscription_uuid();
		if (uuid)
			await this.loadSubscription(uuid);
	}

	async loadSubscription(uuid)
	{
		const result = await Grape.fetches.getJSON('/api/record', {
			schema: 'events',
			table: 'v_subscription_detail',
			filter: [{ field: 'subscription_uuid', operator: '=', value: uuid }],
			limit: 1
		});

		if (!result.records || result.records.length === 0)
		{
			Grape.alerts.alert({ type: 'error', title: 'Not Found', message: 'Subscription not found' });
			return;
		}

		const sub = result.records[0];
		this.viewModel.subscription_detail(sub);
		this.viewModel.initialValue(sub.config_data || {});
		this.viewModel.active(sub.active);
		this.viewModel.idx(sub.idx);
		this.viewModel.defer(sub.defer);
		this.viewModel.event_data_match(sub.event_data_match ? JSON.stringify(sub.event_data_match) : '');
		this.viewModel.json_map_definition_id(sub.json_map_definition_id);

		this.viewModel.selected_action_qname(`${sub.action_namespace}.${sub.action_name}`);
		this.viewModel.selected_namespace(sub.event_namespace);
		this.viewModel.selected_object(sub.event_object_type);
		this.viewModel.selected_name(sub.event_name);

		await this.loadActionLogs(sub.subscription_id);
		await this.loadEventInfo(sub.event_namespace, sub.event_object_type, sub.event_name);
		await this.loadActionInfo(`${sub.action_namespace}.${sub.action_name}`);
	}

	async loadEventNamespaces()
	{
		this.viewModel.raw_event_data(await Grape.cache.fetch('EventNames'));
		const namespaces = [...new Set(
			this.viewModel.raw_event_data().map(x => x.namespace)
		)];
		this.viewModel.event_namespaces(namespaces);
	}

	getEventObjectTypes()
	{
		const objectTypes = [...new Set(
			this.viewModel.raw_event_data()
				.filter(x => x.namespace === this.viewModel.selected_namespace())
				.map(x => x.object_type)
		)];
		this.viewModel.event_objects(objectTypes);
	}

	getEventNames()
	{
		const names = this.viewModel.raw_event_data()
			.filter(x =>
				x.namespace === this.viewModel.selected_namespace() &&
				x.object_type === this.viewModel.selected_object()
			)
			.map(x => x.name);
		this.viewModel.event_names(names);
	}

	async loadActions()
	{
		const response = await Grape.fetches.getJSON('/api/record', {
			schema: 'events',
			table: 'v_actions'
		});
		this.viewModel.raw_action_data(response.records || []);

		const actions = this.viewModel.raw_action_data().map(x => ({
			action_qname: `${x.namespace}.${x.name}`,
			ui_fields: x.ui_fields || [],
			description: x.description
		}));
		this.viewModel.actions(actions);
	}

	async loadActionLogs(subscriptionId)
	{
		const result = await Grape.fetches.getJSON('/api/record', {
			schema: 'events',
			table: 'v_action_log',
			filter: [{ field: 'subscription_id', operator: '=', value: subscriptionId }],
			sortfield: 'date_inserted',
			sortorder: 'DESC',
			limit: 50
		});
		this.viewModel.action_logs(result.records || []);
	}

	async loadEventInfo(namespace, objectType, name)
	{
		const result = await Grape.fetches.getJSON('/api/record', {
			schema: 'events',
			table: 'v_events',
			filter: [
				{ field: 'namespace', operator: '=', value: namespace },
				{ field: 'object_type', operator: '=', value: objectType },
				{ field: 'name', operator: '=', value: name }
			],
			limit: 1
		});
		if (result.records && result.records.length > 0)
			this.viewModel.event_info(result.records[0]);
	}

	async loadActionInfo(qname)
	{
		const parts = qname.split('.');
		if (parts.length < 2)
			return;

		const actionNs = parts[0];
		const actionName = parts.slice(1).join('.');

		const result = await Grape.fetches.getJSON('/api/record', {
			schema: 'events',
			table: 'v_actions',
			filter: [
				{ field: 'namespace', operator: '=', value: actionNs },
				{ field: 'name', operator: '=', value: actionName }
			],
			limit: 1
		});
		if (result.records && result.records.length > 0)
			this.viewModel.action_info(result.records[0]);
	}
}

export default {
	route: '[/]ui/events/subscription/:subscription_uuid',
	page_class: ManageSubscriptionPage,
	template: template
};
